export interface MailCsvContact {
  email: string;
  name?: string;
}

export interface MailCsvSummary {
  totalDataRows: number;
  validRows: number;
  skippedRows: number;
  emptyRows: number;
  missingEmailRows: number;
  invalidEmailRows: number;
  duplicateRows: number;
}

export interface MailCsvParseResult {
  contacts: MailCsvContact[];
  summary: MailCsvSummary;
  errors: string[];
  warnings: string[];
}

const MAX_CSV_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function emptySummary(): MailCsvSummary {
  return {
    totalDataRows: 0,
    validRows: 0,
    skippedRows: 0,
    emptyRows: 0,
    missingEmailRows: 0,
    invalidEmailRows: 0,
    duplicateRows: 0,
  };
}

function parseCsvRows(csvText: string): { rows: string[][]; error?: string } {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i += 1) {
    const char = csvText[i];

    if (inQuotes) {
      if (char === '"') {
        if (csvText[i + 1] === '"') {
          currentField += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        currentField += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ',') {
      currentRow.push(currentField.trim());
      currentField = '';
      continue;
    }

    if (char === '\n') {
      currentRow.push(currentField.trim());
      rows.push(currentRow);
      currentRow = [];
      currentField = '';
      continue;
    }

    if (char === '\r') {
      continue;
    }

    currentField += char;
  }

  if (inQuotes) {
    return { rows, error: 'Invalid CSV format: missing closing quote.' };
  }

  const hasPendingData = currentField.length > 0 || currentRow.length > 0;
  if (hasPendingData) {
    currentRow.push(currentField.trim());
    rows.push(currentRow);
  }

  return { rows };
}

export function validateMailCsvFile(file: File): string | null {
  const fileName = file.name?.toLowerCase() ?? '';
  const fileType = file.type?.toLowerCase() ?? '';

  const looksLikeCsv =
    fileName.endsWith('.csv') || fileType.includes('csv') || fileType.includes('excel');

  if (!looksLikeCsv) {
    return 'Invalid file type. Please upload a .csv file.';
  }

  if (file.size === 0) {
    return 'CSV file is empty.';
  }

  if (file.size > MAX_CSV_FILE_SIZE_BYTES) {
    return 'CSV file is too large. Maximum size is 5 MB.';
  }

  return null;
}

export function parseMailContactsCsv(csvText: string): MailCsvParseResult {
  const summary = emptySummary();
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!csvText || !csvText.trim()) {
    errors.push('CSV is empty.');
    return { contacts: [], summary, errors, warnings };
  }

  const { rows, error } = parseCsvRows(csvText);
  if (error) {
    errors.push(error);
    return { contacts: [], summary, errors, warnings };
  }

  const nonEmptyRows = rows.filter((row) => row.some((cell) => cell.trim().length > 0));
  if (nonEmptyRows.length === 0) {
    errors.push('CSV is empty.');
    return { contacts: [], summary, errors, warnings };
  }

  const rawHeader = nonEmptyRows[0] ?? [];
  const normalizedHeader = rawHeader.map((cell, index) => {
    const value = cell.trim().toLowerCase();
    return index === 0 ? value.replace(/^\uFEFF/, '') : value;
  });

  const emailIndex = normalizedHeader.indexOf('email');
  const nameIndex = normalizedHeader.indexOf('name');

  if (emailIndex === -1) {
    errors.push('Invalid CSV format. Use headers: email,name');
    return { contacts: [], summary, errors, warnings };
  }

  const dataRows = nonEmptyRows.slice(1);
  summary.totalDataRows = dataRows.length;

  if (dataRows.length === 0) {
    errors.push('CSV has no contact rows. Add at least one row under the headers.');
    return { contacts: [], summary, errors, warnings };
  }

  const seenEmails = new Set<string>();
  const contacts: MailCsvContact[] = [];

  dataRows.forEach((row) => {
    if (!row.some((cell) => cell.trim().length > 0)) {
      summary.emptyRows += 1;
      return;
    }

    const rawEmail = (row[emailIndex] ?? '').trim().toLowerCase();
    const rawName = nameIndex >= 0 ? (row[nameIndex] ?? '').trim() : '';

    if (!rawEmail) {
      summary.missingEmailRows += 1;
      return;
    }

    if (!EMAIL_REGEX.test(rawEmail)) {
      summary.invalidEmailRows += 1;
      return;
    }

    if (seenEmails.has(rawEmail)) {
      summary.duplicateRows += 1;
      return;
    }

    seenEmails.add(rawEmail);
    contacts.push({
      email: rawEmail,
      name: rawName || undefined,
    });
  });

  summary.validRows = contacts.length;
  summary.skippedRows =
    summary.emptyRows + summary.missingEmailRows + summary.invalidEmailRows + summary.duplicateRows;

  if (contacts.length === 0) {
    errors.push('No valid contacts found in CSV.');
    return { contacts, summary, errors, warnings };
  }

  if (summary.skippedRows > 0) {
    warnings.push(
      `${summary.skippedRows} row(s) skipped: ${summary.invalidEmailRows} invalid email, ${summary.missingEmailRows} missing email, ${summary.duplicateRows} duplicate, ${summary.emptyRows} empty.`,
    );
  }

  return { contacts, summary, errors, warnings };
}

export async function parseMailContactsCsvFile(file: File): Promise<MailCsvParseResult> {
  const summary = emptySummary();
  const validationError = validateMailCsvFile(file);

  if (validationError) {
    return {
      contacts: [],
      summary,
      errors: [validationError],
      warnings: [],
    };
  }

  try {
    const csvText = await file.text();
    return parseMailContactsCsv(csvText);
  } catch {
    return {
      contacts: [],
      summary,
      errors: ['Unable to read CSV file. Please try again.'],
      warnings: [],
    };
  }
}
